import { graphql, useStaticQuery } from 'gatsby';
import { NavLinkProps } from '../components/navigation/main/MainNavigationPropTypes';
import { LocaleType } from '../contentful/templates/types';
import useLocale from '../localization/useLocale';
import useContentfulNavigationData from './useContentfulNavigationData';

type PageType = {
  path: string;
  context: {
    pageId: string;
    locale: LocaleType;
  };
};

const query = graphql`
  query LocaleLinkData {
    allSitePage {
      edges {
        node {
          path
          context {
            pageId
            locale
          }
        }
      }
    }
  }
`;

const useContentfulLocaleLinks = (pageId: string | null): NavLinkProps[] => {
  const { allSitePage } = useStaticQuery(query);
  const locale = useLocale();
  if (!pageId) {
    return [];
  }

  // Deduplicate by locale — same entry can appear as both a page and blog post
  const byLocale = new Map<string, NavLinkProps>();
  allSitePage.edges
    .filter(
      ({ node }: { node: PageType }) =>
        node.context?.pageId === pageId && node.context?.locale !== locale,
    )
    .forEach(({ node }: { node: PageType }) => {
      byLocale.set(node.context.locale, {
        to: node.path,
        label: node.context.locale,
      });
    });

  return Array.from(byLocale.values());
};

type StaticPageKeys = {
  [key: string]: {
    [key in LocaleType]: string;
  };
};
const staticPageKeyMap: StaticPageKeys = {
  search: {
    fi: '/haku',
    en: '/en/search',
    de: '/en/search',
    fr: '/en/search',
    sv: '/en/search',
    no: '/en/search',
  },
};

export const staticPageSlugToLocaleLink = (
  locale: LocaleType,
  staticPageKey: string | undefined,
): string | undefined => {
  if (!staticPageKey) {
    return undefined;
  }
  const foundKey = staticPageKeyMap[staticPageKey];
  if (foundKey) {
    return foundKey[locale];
  }
};

const getLocalizedLink = (
  locale: LocaleType,
  pageId: string | null,
  resolvedLink: string | undefined,
  localeLinkPath: string | undefined
): string => {
  const page = useContentfulNavigationData(pageId || undefined);
  const shouldHideLocalizedPage = page?.hideFromLocalization && locale === 'fi';
  const pageLinkNotResolved = page?.slug !== 'en' && !resolvedLink && !localeLinkPath;

  if (shouldHideLocalizedPage || pageLinkNotResolved) return '/en/404';
  if (resolvedLink) return resolvedLink;
  if (localeLinkPath) return localeLinkPath;
  if (locale === 'fi') return '/en';

  return '/';
};

// Maps current locale to the locale shown in the language toggle
const toggleLocale = (locale: LocaleType): LocaleType =>
  locale === 'fi' ? 'en' : 'fi';

export const useLocaleLinks = (
  pageId: string | null,
  staticPageKey?: string,
  localeLinkPath?: string,
): NavLinkProps[] => {
  const locale = useLocale();
  const contentfulLinks = useContentfulLocaleLinks(pageId);
  const page = useContentfulNavigationData(pageId || undefined);

  if (page?.hideFromLocalization) {
    return [];
  }

  if (contentfulLinks.length > 0) {
    return contentfulLinks;
  }

  // pageId set but no other locale pages found — don't show a toggle
  if (pageId && !page) {
    return [];
  }

  const targetLocale = toggleLocale(locale);
  const staticLink = staticPageSlugToLocaleLink(targetLocale, staticPageKey);
  const to = staticLink || localeLinkPath || (targetLocale === 'fi' ? '/' : `/${targetLocale}`);
  return [{ to, label: targetLocale }];
};

const useLocaleLink = (
  pageId: string | null,
  staticPageKey?: string,
  localeLinkPath?: string,
): NavLinkProps => {
  const links = useLocaleLinks(pageId, staticPageKey, localeLinkPath);
  return links[0] || { to: '/en', label: 'en' };
};

export default useLocaleLink;
